import { CircularProgress } from './CircularProgress'
import { DeltaIndicator } from './DeltaIndicator'
import { DrilldownTrigger } from '../drilldown'
import { TabProps } from './types'
import { useFormatters, usePreferences } from '../../contexts/PreferencesContext'
import { useAuth } from '../../contexts/AuthContext'
import { useIsSupplier } from '../../hooks/usePortalMode'
import { usePortalLabels } from '../../lib/portalLabels'
import { useQualityMeasureLabel } from '../../lib/qualityLabels'
import { getDeliveryColor, DeliveryThresholds, getQualityColorFromBands, QualityGradeBands } from './SiteKPIComponents'
import { NO_ARREARS_SNAPSHOT, NO_ARREARS_SNAPSHOT_FOOT, NO_ARREARS_SNAPSHOT_HINT, arrearsTileDrillFilters } from './arrearsSnapshot'
import { showsOtd, showsOtif } from './deliveryMetricMode'
import { useNavigate } from 'react-router-dom'

interface KPIMetricsSectionProps {
  data: TabProps['data'];
  currency: TabProps['currency'];
  drilldownFilters: Record<string, string>;
  /** CR-5557: human-readable timeframe label, e.g. "All time" or "1 May – 31 May 2026". */
  periodLabel?: string;
  onOTIFClick: () => void;
  onOTDClick: () => void;
}

export function KPIMetricsSection({ data, currency, drilldownFilters, periodLabel, onOTIFClick, onOTDClick }: KPIMetricsSectionProps) {
  // OOV is a current-state KPI (open orders right now). The dashboard date range
  // applies to time-series KPIs (PPM/OTIF/OTD/Spend) but is meaningless for
  // "what's open today", so its drilldown drops date_from/date_to and shows ALL
  // open orders. Historical Arrears keeps the window whenever it reads a
  // month-end (CR-7270 rework, arrearsTileDrillFilters).
  const drilldownFiltersNoDate = Object.fromEntries(
    Object.entries(drilldownFilters).filter(
      ([k]) => k !== 'date_from' && k !== 'date_to',
    ),
  );
  const { formatCurrency, formatCompactCurrency } = useFormatters()
  const { user } = useAuth()
  const isSupplierView = useIsSupplier()
  const navigate = useNavigate()
  const portalLabels = usePortalLabels(user?.platform)
  const qualityLabel = useQualityMeasureLabel()
  // OTIF/KPI percentage decimals from Admin Setup -> Regional -> Decimal
  // Precision. 0 keeps the historical whole-number ring labels; 1-2 shows
  // e.g. 8.6%. Without this the ring rounded regardless of the setting, so a
  // company that configured 2 decimals still read a whole number here.
  const { preferences } = usePreferences()
  const kpiDecimals = preferences.kpi_percent_decimals ?? 0

  // CR-4641 round 2: chart visibility follows the company-wide
  // delivery_metric_mode admin override (CR-8697: incl. 'none').
  // METRIC-DEFINITIONS Q13 (wave 3a): a null OTIF is "N/A" — nothing closed
  // or overdue to divide by — and the ring RENDERS it as N/A, not hidden.
  const showOTIF = showsOtif(data.delivery_metric_mode)
  const showOTD = showsOtd(data.delivery_metric_mode) && data.delivery.otd !== null
  const showPPM = data.quality.ppm !== null

  // CR-6267: customer-configured OTIF colour bands for the KPI cards.
  const otifThresholds: DeliveryThresholds = data.targets?.otif_grade_bands ?? []
  // OTD shares OTIF's bands (both are delivery metrics on the same scale)
  const otdThresholds: DeliveryThresholds = otifThresholds
  // CR-6652: customer-configured PPM colour bands for the KPI cards.
  const ppmGradeBands: QualityGradeBands = data.targets?.ppm_grade_bands ?? []

  // CR-4670: Unified cell layout so sub-labels and values line up horizontally
  // across the three cards regardless of content type (chip / donut / text /
  // currency). Each cell has three fixed-height rows: label, value, footnote.
  const labelClass = 'text-xs text-muted-foreground font-medium uppercase tracking-wide leading-tight h-8 flex items-center justify-center text-center px-1'
  const valueClass = 'h-24 flex items-center justify-center w-full'
  const footClass = 'h-6 flex items-center justify-center'
  // The period's month-end arrears were never captured: say so rather than print £0.
  const arrearsNotCaptured = data.cost.historicalArrearsSource === 'none'

  return (
    <div className="bg-card rounded-lg shadow-sm p-4" data-tour="dashboard-kpis">
      {/* CR-5557: surface the timeframe used for the KPI calculations so
          users can validate the metric. Hidden when the parent doesn't
          pass one, to stay backwards-compatible with non-Default tabs. */}
      {periodLabel && (
        <div className="flex items-center justify-end mb-2 -mt-1">
          <span className="text-xs text-muted-foreground">
            Period: <span className="font-medium text-foreground">{periodLabel}</span>
          </span>
        </div>
      )}
      <div className="grid grid-cols-3 gap-4 items-stretch">
        {/* Quality Section — CR-4542: PPM + NCRs only */}
        <div className="border border-red-200 bg-red-50/30 rounded-lg p-4 flex flex-col">
          <h3 className="text-center font-semibold text-red-600 mb-4 text-sm uppercase tracking-wide">Quality</h3>
          {/* CR-4641 round 2: PPM card hides when include_in_ncr_ppm filter
              empties the queryset (data.quality.ppm === null). NCR card always
              renders; layout collapses to single column when PPM is hidden. */}
          <div className={`grid gap-3 items-start flex-1 ${showPPM ? 'grid-cols-2' : 'grid-cols-1'}`}>
            {showPPM && (
              <DrilldownTrigger metric="ppm" filters={drilldownFilters}>
                <div className="flex flex-col items-center">
                  <p className={labelClass}>{qualityLabel}</p>
                  <div className={valueClass}>
                    <div
                      className="text-white px-3 py-2 rounded-md"
                      style={{ backgroundColor: getQualityColorFromBands(data.quality.ppm ?? 0, ppmGradeBands, 0) }}
                    >
                      <p className="text-2xl font-bold leading-none">{Math.round(data.quality.ppm ?? 0).toLocaleString()}</p>
                    </div>
                  </div>
                  <div className={footClass}>
                    {data.deltas?.ppm && (
                      <DeltaIndicator delta={data.deltas.ppm} inverse={true} format="number" />
                    )}
                  </div>
                </div>
              </DrilldownTrigger>
            )}

            {/* CR-6277: the tile's own drill-down (ncr_date, no drafts) - lists exactly what it counts */}
            <DrilldownTrigger metric="ncr_tile" filters={drilldownFilters}>
              <div className="flex flex-col items-center">
                <p className={labelClass}>NCRs</p>
                <div className={valueClass}>
                  <div className="text-3xl font-bold leading-none">
                    {data.quality.ncrs}
                  </div>
                </div>
                <div className={footClass}>
                  {data.deltas?.ncrs && (
                    <DeltaIndicator delta={data.deltas.ncrs} inverse={true} format="number" />
                  )}
                </div>
              </div>
            </DrilldownTrigger>
          </div>
        </div>

        {/* Delivery Section — CR-4542: OTIF + OTD + Appeals */}
        <div className="border border-green-200 bg-green-50/30 rounded-lg p-4 flex flex-col">
          <h3 className="text-center font-semibold text-green-600 mb-4 text-sm uppercase tracking-wide">Delivery</h3>
          <div className={`grid gap-3 items-start flex-1 ${showOTIF && showOTD ? 'grid-cols-3' : 'grid-cols-2'}`}>
            {showOTIF && (
              <div
                className="flex flex-col items-center cursor-pointer hover:ring-2 hover:ring-primary/20 rounded-lg transition-all"
                onClick={onOTIFClick}
                role="button"
                tabIndex={0}
                aria-label="Drill down into OTIF data"
              >
                <p className={labelClass}>OTIF</p>
                <div className={valueClass}>
                  <CircularProgress
                    percentage={data.quality.otif}
                    color={data.quality.otif === null ? '#9ca3af' : getDeliveryColor(data.quality.otif, otifThresholds, kpiDecimals)}
                    size={80}
                    strokeWidth={8}
                    decimals={kpiDecimals}
                  />
                </div>
                <div className={footClass}>
                  {data.deltas?.otif && (
                    <DeltaIndicator delta={data.deltas.otif} inverse={false} format="percentage" />
                  )}
                </div>
              </div>
            )}

            {showOTD && (
              <div
                className="flex flex-col items-center cursor-pointer hover:ring-2 hover:ring-primary/20 rounded-lg transition-all"
                onClick={onOTDClick}
                role="button"
                tabIndex={0}
                aria-label="Drill down into OTD data"
              >
                <p className={labelClass}>OTD</p>
                <div className={valueClass}>
                  <CircularProgress
                    percentage={data.delivery.otd ?? 0}
                    color={getDeliveryColor(data.delivery.otd ?? 0, otdThresholds, kpiDecimals)}
                    size={80}
                    strokeWidth={8}
                    decimals={kpiDecimals}
                  />
                </div>
                <div className={footClass}>
                  {data.deltas?.otd && (
                    <DeltaIndicator delta={data.deltas.otd} inverse={false} format="percentage" />
                  )}
                </div>
              </div>
            )}

            <DrilldownTrigger metric="appeals" filters={drilldownFilters}>
              <div className="flex flex-col items-center">
                <p className={labelClass}>Appeals</p>
                <div className={valueClass}>
                  <div className="text-3xl font-bold leading-none">
                    {data.delivery.appeals}
                  </div>
                </div>
                {/* CR-7167: Show action required count so users know appeals need
                    their response; clicking opens Submitted Appeals on Action Required.
                    CR-8239: what waits on each side is the backend's rule (a supplier's
                    are the appeals the customer queried), picked by the portal view the
                    tab and badge also use. */}
                <div className={footClass}>
                  {(() => {
                    const breakdown = data.delivery.appealsBreakdown;
                    const actionCount = isSupplierView
                      ? (breakdown?.action_required_supplier ?? 0)
                      : (breakdown?.action_required_customer ?? 0);
                    if (actionCount > 0) {
                      return (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent DrilldownTrigger from firing
                            // Navigate to Purchasing Deliveries tab with appeals subtab
                            // and action_required tab pre-selected. Use 'appealtab' param
                            // (not 'appealstatus') to avoid deliveryFilterUrlSync mapping
                            // it to the deliveries API filter.
                            navigate('/purchasing?tab=delivery&subtab=appeals&appealtab=action_required');
                          }}
                          className="text-xs font-semibold text-orange-600 bg-orange-100 px-2 py-0.5 rounded-full hover:bg-orange-200 transition-colors cursor-pointer"
                        >
                          {actionCount} need action
                        </button>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>
            </DrilldownTrigger>
          </div>
        </div>

        {/* Cost Section — CR-4542: Open Order Value + Total Spend + Historical Arrears */}
        <div className="border border-blue-200 bg-blue-50/30 rounded-lg p-4 flex flex-col">
          <h3 className="text-center font-semibold text-blue-600 mb-4 text-sm uppercase tracking-wide">Cost</h3>
          <div className="grid grid-cols-3 gap-3 items-start flex-1">
            <DrilldownTrigger metric="open_order_value" filters={drilldownFiltersNoDate}>
              <div className="flex flex-col items-center">
                <p className={labelClass}>{portalLabels.openOrderValue}</p>
                <div className={valueClass}>
                  {/* Compact figure (₹76.97Cr) so three long INR strings never
                      overlap in the tile; the exact value lives in the tooltip. */}
                  <div className="text-xl font-bold leading-none text-center" title={formatCurrency(data.delivery.openOrderValue)}>{formatCompactCurrency(data.delivery.openOrderValue)}</div>
                </div>
                <div className={footClass}>
                  {data.deltas?.open_order_value ? (
                    <DeltaIndicator delta={data.deltas.open_order_value} inverse={true} format="currency" currency={currency.currentCurrency} />
                  ) : (
                    <p className="text-[10px] text-muted-foreground">All orders</p>
                  )}
                </div>
              </div>
            </DrilldownTrigger>

            <DrilldownTrigger metric="spend" filters={drilldownFilters}>
              <div className="flex flex-col items-center">
                <p className={labelClass}>{portalLabels.totalSpend}</p>
                <div className={valueClass}>
                  <div className="text-xl font-bold leading-none text-center" title={formatCurrency(data.cost.totalSpend)}>{formatCompactCurrency(data.cost.totalSpend)}</div>
                </div>
                <div className={footClass}>
                  {/* CR-8867: as the Cost tab's Cost Reduction tile — a rise in spend is red, a fall green */}
                  {data.deltas?.total_spend && (
                    <DeltaIndicator delta={data.deltas.total_spend} inverse={true} format="currency" currency={currency.currentCurrency} />
                  )}
                </div>
              </div>
            </DrilldownTrigger>

            <DrilldownTrigger metric="arrears" filters={arrearsTileDrillFilters(drilldownFilters, drilldownFiltersNoDate, data.cost.historicalArrearsSource)}>
              <div className="flex flex-col items-center">
                <p className={labelClass}>{portalLabels.historicalArrearsValue}</p>
                <div className={valueClass}>
                  {arrearsNotCaptured ? (
                    <div className="text-sm font-semibold text-muted-foreground leading-tight text-center" title={NO_ARREARS_SNAPSHOT_HINT}>{NO_ARREARS_SNAPSHOT}</div>
                  ) : (
                    <div className="text-xl font-bold leading-none text-center" title={formatCurrency(data.cost.historicalArrears)}>{formatCompactCurrency(data.cost.historicalArrears)}</div>
                  )}
                </div>
                <div className={footClass}>
                  {arrearsNotCaptured ? (
                    <p className="text-[10px] text-muted-foreground" title={NO_ARREARS_SNAPSHOT_HINT}>{NO_ARREARS_SNAPSHOT_FOOT}</p>
                  ) : data.deltas?.arrears_value ? (
                    <DeltaIndicator delta={data.deltas.arrears_value} inverse={true} format="currency" currency={currency.currentCurrency} />
                  ) : (
                    <p className="text-[10px] text-muted-foreground">All orders</p>
                  )}
                </div>
              </div>
            </DrilldownTrigger>
          </div>
        </div>
      </div>
    </div>
  )
}
